import { useState, useRef } from "react";
import Papa from "papaparse";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2, FileSpreadsheet } from "lucide-react";

const SKIP = "__skip__";
const FIELDS = [
  { key: "title", label: "Title", required: true },
  { key: "description", label: "Description" },
  { key: "priority", label: "Priority (low/medium/high/urgent)" },
  { key: "category", label: "Category (by name)" },
  { key: "status", label: "Status (by name)" },
  { key: "assignee", label: "Assignee (name or email)" },
  { key: "location", label: "Location (by name)" },
  { key: "due_date", label: "Due date (YYYY-MM-DD)" },
];

function autoMap(headers, key, label) {
  const target = label.split(" ")[0].toLowerCase();
  const found = headers.find((h) => h.trim().toLowerCase() === key || h.trim().toLowerCase() === target);
  return found || SKIP;
}

export default function TaskImportDialog({ open, onOpenChange, onImported }) {
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const reset = () => { setHeaders([]); setRows([]); setMapping({}); setFileName(""); if (fileRef.current) fileRef.current.value = ""; };

  const handleFile = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const hdrs = (res.meta.fields || []).filter((h) => h && h.trim() !== "");
        setHeaders(hdrs);
        setRows(res.data);
        const initial = {};
        FIELDS.forEach((f) => { initial[f.key] = autoMap(hdrs, f.key, f.label); });
        setMapping(initial);
      },
      error: () => toast.error("Could not parse CSV file"),
    });
  };

  const doImport = async () => {
    if (mapping.title === SKIP || !mapping.title) return toast.error("Map the Title column first");
    const payloadRows = rows.map((r) => {
      const out = {};
      FIELDS.forEach((f) => {
        const col = mapping[f.key];
        if (col && col !== SKIP) out[f.key] = (r[col] ?? "").toString();
      });
      return out;
    }).filter((r) => (r.title || "").trim());
    if (payloadRows.length === 0) return toast.error("No rows with a title to import");
    setImporting(true);
    try {
      const { data } = await api.post("/tasks/bulk-import", { rows: payloadRows });
      toast.success(`Imported ${data.created} task(s)${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Import tasks from CSV</DialogTitle>
          <DialogDescription>Upload a CSV file and map its columns to task fields.</DialogDescription>
        </DialogHeader>

        {headers.length === 0 ? (
          <button onClick={() => fileRef.current?.click()} data-testid="csv-upload-trigger" className="flex w-full flex-col items-center gap-2 border-2 border-dashed border-zinc-300 bg-zinc-50 px-4 py-10 text-sm text-zinc-500 transition-colors hover:border-[#e84e34] hover:text-[#e84e34]">
            <Upload className="h-6 w-6" />
            Click to choose a CSV file
          </button>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
              <FileSpreadsheet className="h-4 w-4 text-[#e84e34]" /> {fileName} · {rows.length} row(s) detected
              <button onClick={reset} className="ml-auto text-zinc-400 hover:text-[#e84e34]">Change</button>
            </div>
            <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">Map columns</p>
            <div className="space-y-2">
              {FIELDS.map((f) => (
                <div key={f.key} className="grid grid-cols-2 items-center gap-2">
                  <label className="text-sm text-zinc-700">{f.label}{f.required && <span className="text-[#e84e34]"> *</span>}</label>
                  <Select value={mapping[f.key] || SKIP} onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v }))}>
                    <SelectTrigger data-testid={`map-${f.key}`} className="h-8 rounded-none text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SKIP}>— skip —</SelectItem>
                      {headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        )}
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} data-testid="csv-file-input" />

        <DialogFooter>
          <button onClick={doImport} disabled={importing || headers.length === 0} data-testid="csv-import-submit" className="flex items-center gap-2 bg-[#e84e34] px-4 py-2 text-sm font-medium text-white hover:bg-[#c73e26] disabled:opacity-50">
            {importing && <Loader2 className="h-4 w-4 animate-spin" />} Import {rows.length ? `${rows.length} row(s)` : ""}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
